import { SlidesPerRow } from "@components/swagmagic/dynamic/horizontal-carousel-wrapper";
import { ComputedSpans } from "@components/swagmagic/dynamic/slices/catalog-entries-grid";
import {
  CATALOG_HOME_LINK_TYPE,
  CATEGORY_HOME_LINK_TYPE,
  CATEGORY_PRODUCT_LISTING_LINK_TYPE,
  EXTERNAL_LINK_TYPE,
  LinkWithLangType,
  OTHER_LINK_TYPE,
} from "@stadium/shared/src/constants/stores/routes";

export const SWAGMAGIC_HEADER = "SwagmagicHeader";

export const DATA_PROVIDER_STORE_MENU = "store-menu";
export const CATEGORY_BRAND_CAROUSEL = "category-brand-carousel";

export const DEFAULT = "Default";
export const CUSTOM = "Custom";

export type ViewType = typeof DEFAULT | typeof CUSTOM;

export type DynamicSlicesData = {
  isMenuAndStoreDataLoading?: boolean;
  store?: any;
  menu?: any;
  parentCategories: any[];
  allCategories: any[];
  menuMapping?: Record<string, any>;
  countryIso?: string;
};

export const CE_01 = "CE-01";
export const CE_02 = "CE-02";
export const CE_03 = "CE-03";
export const CE_04 = "CE-04";
export const CE_05 = "CE-05";
export const CE_06 = "CE-06";
export const CE_08 = "CE-08";
export const CE_09 = "CE-09";
export const CE_12 = "CE-12";
export const CE_14 = "CE-14";

export const PL_01 = "PL-01";
export const PL_04 = "PL-04";
export const PL_05 = "PL-05";
export const PL_06 = "PL-06";
export const PL_07 = "PL-07";
export const PL_10 = "PL-10";
export const PL_11 = "PL-11";
export const PL_12 = "PL-12";

export type CategoryVariant =
  | typeof CE_01
  | typeof CE_02
  | typeof CE_03
  | typeof CE_04
  | typeof CE_05
  | typeof CE_06
  | typeof CE_08
  | typeof CE_09
  | typeof CE_12
  | typeof CE_14;

export type ProductListingVariant =
  | typeof PL_01
  | typeof PL_04
  | typeof PL_05
  | typeof PL_06
  | typeof PL_07
  | typeof PL_10
  | typeof PL_11
  | typeof PL_12;

export const INFO_CATEGORY_CARD_VARIANT = [CE_01, CE_02, CE_05, CE_06, CE_09, CE_12];
export const DETAILED_CATEGORY_CARD_VARIANT = [CE_03, CE_04];
export const GRID_VARIANT = [CE_08, CE_09];
export const DESCRIPTION_CARD_VARIANT = [PL_06, PL_07, PL_10, PL_11, PL_12];
export const SMALL_DESCRIPTION_CARD_VARIANT = [PL_06, PL_11, PL_12];

export const PRODUCT_LISTING_CAROUSEL_VARIANT_KEYS = [
  PL_01,
  PL_04,
  PL_05,
  PL_06,
  PL_07,
  PL_10,
  PL_11,
  PL_12,
];

export const CATALOG_ENTRIES_VARIANT = [
  {
    label: CE_01,
    value: CE_01,
  },
  {
    label: CE_02,
    value: CE_02,
  },
  {
    label: CE_03,
    value: CE_03,
  },
  {
    label: CE_04,
    value: CE_04,
  },
  {
    label: CE_05,
    value: CE_05,
  },
  {
    label: CE_06,
    value: CE_06,
  },
  {
    label: CE_08,
    value: CE_08,
  },
  {
    label: CE_09,
    value: CE_09,
  },
  {
    label: CE_12,
    value: CE_12,
  },
  {
    label: CE_14,
    value: CE_14,
  },
];

export const PRODUCT_LISTING_CAROUSEL_VARIANT = [
  {
    label: PL_01,
    value: PL_01,
  },
  {
    label: PL_04,
    value: PL_04,
  },
  {
    label: PL_05,
    value: PL_05,
  },
  {
    label: PL_06,
    value: PL_06,
  },
  {
    label: PL_07,
    value: PL_07,
  },
  {
    label: PL_10,
    value: PL_10,
  },
  {
    label: PL_11,
    value: PL_11,
  },
  {
    label: PL_12,
    value: PL_12,
  },
];

type BreakpointConfig = {
  superLargeDesktop: { slides: number; rows: number };
  web: { slides: number; rows: number };
  ipad: { slides: number; rows: number };
  tablet?: { slides: number; rows: number };
  mobile: { slides: number; rows: number };
};

export const DEFAULT_CONFIG: BreakpointConfig = {
  superLargeDesktop: { slides: 6, rows: 1 },
  web: { slides: 6, rows: 1 },
  ipad: { slides: 4, rows: 1 },
  mobile: { slides: 2, rows: 1 },
};

export const GRID_CONFIG = {
  [CE_08]: {
    columns: 4,
    tabletColumns: 4,
    mobileColumns: 2,
  },
  [CE_09]: {
    columns: 6,
    tabletColumns: 4,
    mobileColumns: 2,
  },
};

const CAROUSEL_SLIDES_CONFIG: Record<CategoryVariant, BreakpointConfig> = {
  [CE_01]: {
    superLargeDesktop: { slides: 6, rows: 1 },
    web: { slides: 6, rows: 1 },
    ipad: { slides: 6, rows: 1 },
    mobile: { slides: 2.5, rows: 1 },
  },
  [CE_02]: {
    superLargeDesktop: { slides: 6, rows: 1 },
    web: { slides: 6, rows: 1 },
    ipad: { slides: 6, rows: 1 },
    mobile: { slides: 2.5, rows: 1 },
  },
  [CE_03]: {
    superLargeDesktop: { slides: 3, rows: 2 },
    web: { slides: 3, rows: 2 },
    ipad: { slides: 1.75, rows: 1 },
    mobile: { slides: 1, rows: 3 },
  },
  [CE_04]: {
    superLargeDesktop: { slides: 3, rows: 2 },
    web: { slides: 3, rows: 2 },
    ipad: { slides: 1.75, rows: 1 },
    mobile: { slides: 1, rows: 3 },
  },
  [CE_05]: {
    superLargeDesktop: { slides: 6, rows: 1 },
    web: { slides: 6, rows: 1 },
    ipad: { slides: 6, rows: 1 },
    mobile: { slides: 2.25, rows: 1 },
  },
  [CE_06]: {
    superLargeDesktop: { slides: 9.75, rows: 1 },
    web: { slides: 9.75, rows: 1 },
    ipad: { slides: 5.25, rows: 1 },
    mobile: { slides: 3.75, rows: 1 },
  },
  [CE_08]: DEFAULT_CONFIG,
  [CE_09]: DEFAULT_CONFIG,
  [CE_12]: {
    superLargeDesktop: { slides: 6, rows: 1 },
    web: { slides: 6, rows: 1 },
    ipad: { slides: 4.25, rows: 1 },
    mobile: { slides: 2.5, rows: 1 },
  },
  [CE_14]: {
    superLargeDesktop: { slides: 7, rows: 1 },
    web: { slides: 7, rows: 1 },
    ipad: { slides: 7, rows: 1 },
    mobile: { slides: 2.75, rows: 1 },
  },
};

const PRODUCT_LISTING_CAROUSEL_SLIDES_CONFIG: Record<ProductListingVariant, BreakpointConfig> = {
  [PL_01]: {
    superLargeDesktop: { slides: 4, rows: 1 },
    web: { slides: 4, rows: 1 },
    ipad: { slides: 4, rows: 1 },
    mobile: { slides: 2, rows: 2 },
  },
  [PL_04]: {
    superLargeDesktop: { slides: 6, rows: 1 },
    web: { slides: 6, rows: 1 },
    ipad: { slides: 6, rows: 1 },
    mobile: { slides: 2.25, rows: 1 },
  },
  [PL_05]: {
    superLargeDesktop: { slides: 6, rows: 1 },
    web: { slides: 6, rows: 1 },
    ipad: { slides: 6, rows: 1 },
    mobile: { slides: 2.25, rows: 1 },
  },
  [PL_06]: {
    superLargeDesktop: { slides: 4, rows: 1 },
    web: { slides: 4, rows: 1 },
    ipad: { slides: 2.25, rows: 1 },
    mobile: { slides: 1, rows: 1 },
  },
  [PL_07]: {
    superLargeDesktop: { slides: 3, rows: 1 },
    web: { slides: 3, rows: 1 },
    ipad: { slides: 2.25, rows: 1 },
    mobile: { slides: 1, rows: 1 },
  },
  [PL_10]: {
    superLargeDesktop: { slides: 3, rows: 1 },
    web: { slides: 3, rows: 1 },
    ipad: { slides: 2.25, rows: 1 },
    mobile: { slides: 1, rows: 1 },
  },
  [PL_11]: {
    superLargeDesktop: { slides: 4, rows: 1 },
    web: { slides: 4, rows: 1 },
    ipad: { slides: 2.25, rows: 1 },
    mobile: { slides: 1, rows: 1 },
  },
  [PL_12]: {
    superLargeDesktop: { slides: 4, rows: 1 },
    web: { slides: 4, rows: 1 },
    ipad: { slides: 2.25, rows: 1 },
    mobile: { slides: 1, rows: 1 },
  },
};

export const getCarouselSlidesPerRow = (variant: CategoryVariant): SlidesPerRow => {
  const config = CAROUSEL_SLIDES_CONFIG[variant] || DEFAULT_CONFIG;
  const { superLargeDesktop, web, ipad, tablet, mobile } = config;

  return {
    slidesPerRow: web,
    superLargeDesktop,
    web,
    ipad,
    ...(tablet && { tablet }),
    mobile,
  };
};

export const getProductListingCarouselSlidesPerRow = (
  variant: ProductListingVariant,
): SlidesPerRow => {
  const config = PRODUCT_LISTING_CAROUSEL_SLIDES_CONFIG[variant] || DEFAULT_CONFIG;
  const { superLargeDesktop, web, ipad, mobile } = config;

  return {
    slidesPerRow: web,
    superLargeDesktop,
    web,
    ipad,
    mobile,
  };
};

export const hideDetailedCategoryCardProps = (variant: CategoryVariant): boolean => {
  return INFO_CATEGORY_CARD_VARIANT.includes(variant);
};

/**
 * Clamps a span value to ensure it's within valid bounds for CSS Grid.
 * Ensures the value is at least 1 and doesn't exceed the maximum number of columns.
 * If the value is undefined, uses the fallback value instead.
 *
 * @param value - The span value to clamp (can be undefined)
 * @param max - Maximum allowed span value (must be at least 1)
 * @param fallback - Fallback value to use if value is undefined
 * @returns A clamped span value between 1 and max (inclusive)
 */
const clampSpan = (value: number | undefined, max: number, fallback: number) =>
  Math.max(1, Math.min(value ?? fallback, Math.max(1, max)));

/**
 * Computes responsive column spans for a card across different breakpoints.
 * Calculates how many grid columns a card should span on desktop, tablet, and mobile.
 * Ensures spans don't exceed available columns for each breakpoint and provides
 * sensible fallbacks when tablet/mobile spans aren't explicitly set.
 *
 * @param card - The card object containing optional colSpan, tabletSpan, and mobileSpan properties
 * @param columns - Total number of columns available on desktop
 * @param tabletColumns - Total number of columns available on tablet (defaults to min of columns and 6)
 * @param mobileColumns - Total number of columns available on mobile
 * @returns An object containing computed colSpan, tabletSpan, and mobileSpan values
 */
export const computeSpans = (
  card: any,
  columns: number,
  tabletColumns: number,
  mobileColumns: number,
): ComputedSpans => {
  const baseSpan = card.colSpan ?? 1;
  const colSpan = clampSpan(baseSpan, columns, 1);

  const tabletMax = tabletColumns ?? Math.min(columns, 6);
  const mobileMax = mobileColumns;

  const tabletSpanValue = card.tabletSpan ?? colSpan;
  const tabletSpan = clampSpan(tabletSpanValue, tabletMax, colSpan);

  const mobileSpanValue = card.mobileSpan ?? colSpan;
  const mobileSpan = clampSpan(mobileSpanValue, mobileMax, colSpan);

  return { colSpan, tabletSpan, mobileSpan };
};

export type HeaderButtonLinkParams = {
  linkType: string;
  otherUrl?: string;
  categorySlug?: string;
  menuIdentifier?: string;
};

export type HeaderButtonLinkResult = {
  type: LinkWithLangType;
  data?: Record<string, string | undefined>;
};

export type LinkResolver = (params: HeaderButtonLinkParams) => HeaderButtonLinkResult | null;

export const LINK_TYPE_RESOLVERS: Record<string, LinkResolver> = {
  [CATEGORY_HOME_LINK_TYPE]: ({ categorySlug }) =>
    categorySlug ? { type: CATEGORY_HOME_LINK_TYPE, data: { code: categorySlug } } : null,
  [CATEGORY_PRODUCT_LISTING_LINK_TYPE]: ({ categorySlug, menuIdentifier }) => {
    if (menuIdentifier) {
      return { type: CATEGORY_PRODUCT_LISTING_LINK_TYPE, data: { menuIdentifier } };
    }
    return categorySlug
      ? { type: CATEGORY_PRODUCT_LISTING_LINK_TYPE, data: { code: categorySlug } }
      : null;
  },
  [CATALOG_HOME_LINK_TYPE]: () => ({ type: CATALOG_HOME_LINK_TYPE }),
  [OTHER_LINK_TYPE]: ({ otherUrl }) =>
    otherUrl ? { type: OTHER_LINK_TYPE, data: { url: otherUrl } } : null,
  [EXTERNAL_LINK_TYPE]: ({ otherUrl }) =>
    otherUrl ? { type: EXTERNAL_LINK_TYPE, data: { url: otherUrl } } : null,
};

export const getHeaderButtonLinkDataAndType = (
  params: HeaderButtonLinkParams,
): HeaderButtonLinkResult => {
  const resolver = LINK_TYPE_RESOLVERS[params.linkType];
  const result = resolver?.(params);
  if (result) return result;
  if (params.menuIdentifier) {
    return {
      type: CATEGORY_PRODUCT_LISTING_LINK_TYPE,
      data: { menuIdentifier: params.menuIdentifier },
    };
  }
  if (params.categorySlug) {
    return { type: CATEGORY_PRODUCT_LISTING_LINK_TYPE, data: { code: params.categorySlug } };
  }
  return { type: CATALOG_HOME_LINK_TYPE };
};
